import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { displayAPI } from '../../services/api';
import type { DisplayRoom, DisplayBooking, ArrivalNotification } from '../../services/api';

// Display hours: 8 AM to 8 PM (12 hours)
const START_HOUR = 8;
const END_HOUR = 20;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const SLOT_WIDTH_PX = 80; // pixels per 30-min slot
const PX_PER_MINUTE = SLOT_WIDTH_PX / 30;
const ROW_HEIGHT_PX = 80; // height per room row
const ROOM_LABEL_WIDTH = 100;
const REFRESH_INTERVAL_MS = 60_000;
const CLOCK_INTERVAL_MS = 15_000;

// Generate time labels (one per 30-min slot)
const TIME_SLOTS: string[] = [];
for (let h = START_HOUR; h < END_HOUR; h++) {
  const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
  const ampm = h >= 12 ? 'PM' : 'AM';
  TIME_SLOTS.push(`${hour}:00 ${ampm}`);
  TIME_SLOTS.push(`${hour}:30 ${ampm}`);
}

const GRID_WIDTH = TOTAL_HOURS * 2 * SLOT_WIDTH_PX;

// Booking type colors (matches member availability calendar)
const BOOKING_COLORS = {
  client: { bg: 'bg-clinic-accent/20', border: 'border-clinic-accent/30', text: 'text-clinic-accent', solid: '#895A4C' },
  self: { bg: 'bg-clinic-gold/20', border: 'border-clinic-gold/30', text: 'text-clinic-gold', solid: '#C2A67A' },
  pod: { bg: 'bg-clinic-brown/20', border: 'border-clinic-brown/30', text: 'text-clinic-brown', solid: '#543E36' },
};

function parseTimeToMinutes(timeStr: string): number {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return 0;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && hours !== 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function getClinicTime(): { time: string; date: string } {
  const now = new Date();
  return {
    time: now.toLocaleTimeString('en-US', { timeZone: 'America/Edmonton', hour: 'numeric', minute: '2-digit', hour12: true }),
    date: now.toLocaleDateString('en-US', { timeZone: 'America/Edmonton', weekday: 'long', month: 'long', day: 'numeric' }),
  };
}

function getCurrentTimePosition(): number | null {
  const now = new Date();
  const edmontonStr = now.toLocaleTimeString('en-US', { timeZone: 'America/Edmonton', hour12: false, hour: '2-digit', minute: '2-digit' });
  const [h, m] = edmontonStr.split(':').map(Number);
  const minutesSinceStart = (h - START_HOUR) * 60 + m;
  if (minutesSinceStart < 0 || minutesSinceStart > TOTAL_HOURS * 60) return null;
  return minutesSinceStart * PX_PER_MINUTE;
}

// =========================================================================
// Booking Block — positioned horizontally within a room row
// =========================================================================

const BookingBlock: React.FC<{ booking: DisplayBooking }> = ({ booking }) => {
  const startMinutes = parseTimeToMinutes(booking.start_time_local);
  const endMinutes = parseTimeToMinutes(booking.end_time_local);
  const offsetMinutes = startMinutes - START_HOUR * 60;
  const widthMinutes = endMinutes - startMinutes;

  if (offsetMinutes < 0 || widthMinutes <= 0) return null;

  const left = offsetMinutes * PX_PER_MINUTE;
  const width = Math.max(widthMinutes * PX_PER_MINUTE, 40);
  const colors = BOOKING_COLORS[booking.booking_type];
  const isNarrow = widthMinutes <= 30;

  return (
    <div
      className={`absolute top-1 bottom-1 ${colors.bg} border ${colors.border} rounded-lg overflow-hidden flex items-center px-2 gap-2 transition-all`}
      style={{ left: `${left}px`, width: `${width}px` }}
    >
      {booking.has_arrived && (
        <div className="absolute top-0 left-0 right-0 bg-clinic-primary text-white text-center text-[8px] font-bold py-0.5 uppercase tracking-wider">
          Arrived
        </div>
      )}

      <div className={`flex-shrink-0 ${isNarrow ? 'w-7 h-7' : 'w-10 h-10'} rounded-full overflow-hidden bg-white shadow-sm ${booking.has_arrived ? 'mt-2' : ''}`}>
        {booking.clinician_photo_url ? (
          <img src={booking.clinician_photo_url} alt={booking.clinician_first_name} className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center font-body font-bold text-white"
            style={{ backgroundColor: colors.solid, fontSize: isNarrow ? '9px' : '12px' }}
          >
            {getInitials(booking.clinician_name)}
          </div>
        )}
      </div>

      {!isNarrow && (
        <div className="min-w-0 flex-1">
          <p className={`${colors.text} font-body font-semibold text-[11px] leading-tight truncate`}>
            {booking.clinician_first_name}
          </p>
          <p className="text-clinic-muted font-body text-[9px] leading-tight">
            {booking.start_time_local}
          </p>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// Arrival Banner
// =========================================================================

const ArrivalBanner: React.FC<{ arrival: ArrivalNotification; onDismiss: () => void }> = ({ arrival, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 30_000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="bg-clinic-primary text-white rounded-card shadow-clinic-lg px-6 py-4 flex items-center justify-between">
      <div>
        <p className="font-display text-lg font-bold">{arrival.clinician_name}</p>
        <p className="font-body text-clinic-cream text-sm">Your {arrival.booking_time} appointment has arrived</p>
      </div>
      <div className="w-3 h-3 bg-clinic-cream rounded-full animate-ping ml-4 flex-shrink-0" />
    </div>
  );
};

// =========================================================================
// Display Board — Rooms (Y) × Time (X), single scroll container
// =========================================================================

const DisplayBoard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [rooms, setRooms] = useState<DisplayRoom[]>([]);
  const [arrivals, setArrivals] = useState<ArrivalNotification[]>([]);
  const [dismissedArrivals, setDismissedArrivals] = useState<Set<string>>(new Set());
  const [clock, setClock] = useState(getClinicTime());
  const [timePosition, setTimePosition] = useState<number | null>(getCurrentTimePosition());
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [scheduleData, arrivalsData] = await Promise.all([
        displayAPI.getSchedule(token),
        displayAPI.getArrivals(token, 2),
      ]);
      setRooms(scheduleData.rooms);
      setArrivals(arrivalsData.arrivals);
      setError('');
    } catch (err: any) {
      if (err.response?.status === 401) setError('Invalid display token.');
      else setError('Unable to load schedule.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial fetch + polling
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Clock update + auto-scroll to follow current time
  useEffect(() => {
    const update = () => {
      setClock(getClinicTime());
      const pos = getCurrentTimePosition();
      setTimePosition(pos);

      // Keep current time centered in the scroll view
      if (scrollRef.current && pos !== null) {
        const viewWidth = scrollRef.current.clientWidth - ROOM_LABEL_WIDTH;
        const target = Math.max(0, pos - viewWidth / 3);
        scrollRef.current.scrollLeft = target;
      }
    };

    // Initial scroll
    update();

    const interval = setInterval(update, CLOCK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loading]);

  const handleDismissArrival = useCallback((key: string) => {
    setDismissedArrivals(prev => new Set(prev).add(key));
  }, []);

  const activeArrivals = arrivals.filter(a => !dismissedArrivals.has(String(a.booking_id)));

  if (!token) {
    return (
      <div className="min-h-screen bg-clinic-dark flex items-center justify-center">
        <p className="text-clinic-muted font-body text-lg">Display board token required.</p>
      </div>
    );
  }

  if (error && !rooms.length) {
    return (
      <div className="min-h-screen bg-clinic-dark flex items-center justify-center">
        <p className="text-clinic-accent font-body text-lg">{error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-clinic-surface flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary" />
      </div>
    );
  }

  const totalGridHeight = rooms.length * ROW_HEIGHT_PX;

  // Rotate the entire UI 90° clockwise for portrait-mounted TVs
  // that cannot be rotated in display settings.
  // The outer div fills the physical (portrait) viewport,
  // the inner div is sized to landscape (w=vh, h=vw) and rotated.
  return (
    <div className="w-screen h-screen overflow-hidden bg-clinic-cream">
    <div
      className="bg-clinic-cream flex flex-col overflow-hidden"
      style={{
        width: '100vh',
        height: '100vw',
        transform: 'rotate(90deg) translateY(-100%)',
        transformOrigin: 'top left',
      }}
    >
      {/* Header */}
      <header className="bg-white shadow-clinic-md border-b border-clinic-primary-lighter px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-7"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div className="text-right">
            <p className="font-display text-xl font-bold text-clinic-dark">{clock.time}</p>
            <p className="font-body text-xs text-clinic-muted">{clock.date}</p>
          </div>
        </div>
      </header>

      {/* Arrival banners */}
      {activeArrivals.length > 0 && (
        <div className="px-4 py-2 space-y-2 flex-shrink-0">
          {activeArrivals.map((arrival) => (
            <ArrivalBanner
              key={arrival.booking_id}
              arrival={arrival}
              onDismiss={() => handleDismissArrival(String(arrival.booking_id))}
            />
          ))}
        </div>
      )}

      {/* Schedule grid — auto-positioned, no manual scrolling */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-hidden">
          <div style={{ width: `${ROOM_LABEL_WIDTH + GRID_WIDTH}px` }}>

            {/* Time header row */}
            <div className="flex sticky top-0 z-20 bg-white border-b border-clinic-primary-lighter">
              <div
                className="flex-shrink-0 bg-white border-r border-clinic-primary-lighter"
                style={{ width: `${ROOM_LABEL_WIDTH}px` }}
              />
              {TIME_SLOTS.map((label, i) => (
                <div
                  key={i}
                  className={`flex-shrink-0 text-center py-2 border-l ${
                    i % 2 === 0 ? 'border-clinic-primary-lighter' : 'border-clinic-primary-lighter/40'
                  }`}
                  style={{ width: `${SLOT_WIDTH_PX}px` }}
                >
                  <p className="font-body text-[10px] text-clinic-muted font-medium">
                    {i % 2 === 0 ? label : ''}
                  </p>
                </div>
              ))}
            </div>

            {/* Room rows */}
            <div className="relative">
              {rooms.map((room) => (
                <div key={room.room_id} className="flex border-b border-clinic-primary-lighter">
                  {/* Room label (sticky left) */}
                  <div
                    className="flex-shrink-0 bg-white border-r border-clinic-primary-lighter flex flex-col items-center justify-center px-2 sticky left-0 z-10"
                    style={{ width: `${ROOM_LABEL_WIDTH}px`, height: `${ROW_HEIGHT_PX}px` }}
                  >
                    <p className="font-body font-bold text-xs text-clinic-dark text-center leading-tight">
                      {room.room_name}
                    </p>
                    <p className="font-body text-[9px] text-clinic-muted">
                      {room.room_type === 'pod' ? 'Pod' : `Cap. ${room.capacity}`}
                    </p>
                  </div>

                  {/* Booking area */}
                  <div className="relative" style={{ width: `${GRID_WIDTH}px`, height: `${ROW_HEIGHT_PX}px` }}>
                    {/* Vertical grid lines */}
                    {TIME_SLOTS.map((_, i) => (
                      <div
                        key={i}
                        className={`absolute top-0 bottom-0 border-l ${
                          i % 2 === 0 ? 'border-clinic-primary-lighter' : 'border-clinic-primary-lighter/40'
                        }`}
                        style={{ left: `${i * SLOT_WIDTH_PX}px` }}
                      />
                    ))}

                    {/* Booking blocks */}
                    {room.bookings.map((booking) => (
                      <BookingBlock key={booking.booking_id} booking={booking} />
                    ))}
                  </div>
                </div>
              ))}

              {/* Current time indicator — single line spanning all rows */}
              {timePosition !== null && (
                <div
                  className="absolute top-0 pointer-events-none z-10"
                  style={{
                    left: `${ROOM_LABEL_WIDTH + timePosition}px`,
                    height: `${totalGridHeight}px`,
                  }}
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-clinic-accent -ml-1 -mt-1" />
                  <div className="w-0.5 bg-clinic-accent" style={{ height: `${totalGridHeight}px`, marginTop: '-4px' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-clinic-primary-lighter px-4 py-1.5 flex-shrink-0">
        <p className="text-center text-[9px] text-clinic-muted font-body">
          &copy; {new Date().getFullYear()} Clinic Studios &middot; Auto-refreshing
        </p>
      </footer>
    </div>
    </div>
  );
};

export default DisplayBoard;
